// The UNTRUSTED-INTAKE SCREEN's vocabulary (plan 101 slice 09, H3 #5393): every raw
// input is dangerous until screened. Triage reads a whole batch in ONE model call
// (`packages/agent/src/triage/raw-task-triage.ts`), so one hostile ask in the batch
// prompt can steer every verdict in it. A separate, tool-less call reads each ask
// ALONE first (`screenAsk`, `packages/agent/src/screen/`) and answers
// {@link ScreenVerdict}; only an `ok` ask reaches the batch.
//
// THREE things live here, and why here: the verdict is STORED (`screen_verdicts`,
// `@developerz/db`) and AUDITED (`raw_task.screened`), and the discard reason is a
// member of the raw task's settled verdict (`RawTaskVerdictSchema`'s `discarded`
// arm) — db, agent, and agents-manager all read the same closed lists, and apps
// never import apps.
//
//   - {@link ScreenVerdictSchema}: `ok` (reason null) | `discard` (a closed reason).
//   - {@link Screened}: a string the screen passed. The triage ask render takes it,
//     so an unscreened ask is a COMPILE error at the render seam, not a review note.
//   - {@link screenedByScreenAskOnly}: the brand's ONE producer. Its name is the
//     fence: `intake-screen.test.ts` refuses a call outside `packages/agent/src/screen/`.

import { z } from 'zod';
import type { RawTaskSource } from '../entities/raw-task/status';
import {
  isModelCallOrigin,
  type TaskEnvelope,
  TaskOrigin,
  TaskOriginSchema,
} from './task-envelope';

/**
 * Why the screen discarded an ask. CLOSED and STORED (`screen_verdicts.reason`,
 * `raw_tasks.verdict ->> 'reason'` on a `discarded` settle), so a member added is a
 * migration of both CHECKs and a member removed is unreadable rows.
 *
 *   - `injection`: the text tries to instruct the machinery that reads it (ignore
 *     previous instructions, change a policy, disable review, exfiltrate a secret).
 *   - `abuse`: harassment, hate, or content no maintainer should be handed as work.
 *   - `spam`: advertising, SEO links, or generated filler with no ask in it.
 *   - `empty`: nothing actionable is written — blank, a template left unfilled.
 */
export const SCREEN_DISCARD_REASONS = ['injection', 'abuse', 'spam', 'empty'] as const;
export const ScreenDiscardReasonSchema = z.enum(SCREEN_DISCARD_REASONS);
export type ScreenDiscardReason = z.infer<typeof ScreenDiscardReasonSchema>;

/**
 * Why the screen could not answer an ask AT ALL — the `cause` on the `unavailable`
 * rows of `prompt.item_screened`. CLOSED and AUDITED (`output.cause`, which the
 * health read groups by), so a bucket is a count and a free string would split one
 * cause across spellings. A value outside this list folds to 'unrecognized' at read,
 * keeping its count rather than being dropped.
 *
 *   - `cap`: the in-flight cap refusal (`too_many_in_flight`) — the claim's OWN
 *     holder over its cap.
 *   - `provider_wall`: the screen unservable — no usable key for the account, or the
 *     key resolver faulted.
 *   - `unwired`: the responder never ran. NO WRITER TODAY: the deps-undefined arm
 *     holds no audit sink, and prod wiring always builds the responder — kept in the
 *     set so the bucket separates at read, never folding into 'unrecognized'.
 *
 * NOT a member, on purpose: `fence`. A refusal over a claim the caller FAILS the
 * fence on writes NO row at all — it is the CALLER's security telemetry, stdout only
 * (`apps/worker/src/fleet/screen-items.ts`), so a foreign runner naming a task
 * cannot mint rows on the victim tenant's chain. The member was removed together
 * with its only writer (#6876 review); a stale stored 'fence' value folds to
 * 'unrecognized' at read, its count kept. A future writer is a type error, which is
 * the point.
 *
 * `null` on a row is the residual 'unavailable' bucket — a per-item model throw,
 * which the agent (`packages/agent/src/screen/items.ts`) cannot attribute. Only the
 * WORKER stamps a non-null cause on the rows it writes.
 */
export const SCREEN_UNAVAILABLE_CAUSES = ['cap', 'unwired', 'provider_wall'] as const;
export const ScreenUnavailableCauseSchema = z.enum(SCREEN_UNAVAILABLE_CAUSES);
export type ScreenUnavailableCause = z.infer<typeof ScreenUnavailableCauseSchema>;

/** The two answers the screen gives. */
export const SCREEN_VERDICTS = ['ok', 'discard'] as const;

/**
 * What the screen answered about ONE ask's text. A DISCRIMINATED UNION, so the
 * nonsense pairs — `ok` with a reason, `discard` without one — are unspellable
 * rather than refused at read. Strict members: a model-authored object carrying an
 * extra key is refused, never silently stripped. The model's own output schema is
 * the agent seat's (`packages/agent/src/screen/`); whatever it is, its output is
 * parsed through THIS before it is stored, audited or acted on.
 */
export const ScreenVerdictSchema = z.discriminatedUnion('verdict', [
  z.strictObject({ verdict: z.literal('ok'), reason: z.null() }),
  z.strictObject({ verdict: z.literal('discard'), reason: ScreenDiscardReasonSchema }),
]);
export type ScreenVerdict = z.infer<typeof ScreenVerdictSchema>;

declare const ScreenedBrand: unique symbol;

/**
 * A string the intake screen PASSED. Assignable to `string` (a reader needs nothing),
 * never assignable FROM one: a plain string reaching a `Screened<string>` parameter is
 * a type error (`intake-screen.test.ts` pins it with `@ts-expect-error`).
 *
 * What it attests is narrow and stated: THIS text got an `ok` from the screen at the
 * prompt version the caller used. It does not make the text safe to execute — the
 * reader still fences it as data — and a string derived from it (a slice, a
 * concatenation) is a plain `string` again, which is the point.
 */
export type Screened<T extends string = string> = T & { readonly [ScreenedBrand]: true };

/**
 * THE producer of {@link Screened}, and it is for `screenAsk` ONLY
 * (`packages/agent/src/screen/`). The name says so because TypeScript cannot: an
 * exported function is callable from anywhere, so the fence is `intake-screen.test.ts`,
 * which scans `apps/` and `packages/` and refuses a call outside that directory.
 *
 * It takes the `ok` verdict it is minting from, so a `discard` cannot be branded
 * by a caller that forgot to branch — that is a compile error, not a review note.
 */
export function screenedByScreenAskOnly<T extends string>(
  text: T,
  verdict: Extract<ScreenVerdict, { verdict: 'ok' }>,
): Screened<T> {
  if (verdict.verdict !== 'ok') {
    throw new Error(
      `screenedByScreenAskOnly: refused to brand text under a '${String(verdict.verdict)}' verdict — only an 'ok' screen verdict mints Screened<string>`,
    );
  }
  return text as Screened<T>;
}

// ─── #7785 owner ruling: a repository's developers are not screened ─────────

declare const DeveloperTextBrand: unique symbol;

/**
 * Text the screen did NOT read, because a developer of the repository wrote it (#7785: org
 * members, a personal repo's owner and collaborators, outside collaborators with write). NOT a
 * {@link Screened}: that brand attests an `ok` from the screen, and minting one here would lie
 * to every reader of the audit and the type. Like `Screened`, it does not make the text safe to
 * execute: the render still fences it as data, and review still runs on what it produces. A
 * stolen account is out of scope by the same ruling.
 */
export type DeveloperText<T extends string = string> = T & { readonly [DeveloperTextBrand]: true };

/** What a render may quote: text the screen passed, or text a repo's developer wrote. */
export type AdmittedText = Screened | DeveloperText;

/**
 * THE producer of {@link DeveloperText}, for the screen's own seam only
 * (`packages/agent/src/screen/`), fenced by `intake-screen.test.ts` like
 * {@link screenedByScreenAskOnly}. It takes the standing it is minting from, so an `external`
 * or `unknown` answer cannot be branded by a caller that forgot to branch.
 */
export function developerTextByScreenOnly<T extends string>(
  text: T,
  standing: Extract<RepoDeveloperStanding, 'developer'>,
): DeveloperText<T> {
  if (standing !== 'developer') {
    throw new Error(
      `developerTextByScreenOnly: refused to brand text under standing '${String(standing)}' — only 'developer' skips the screen`,
    );
  }
  return text as DeveloperText<T>;
}

/**
 * One author's standing on one repository, as the repo answers it. `unknown` is the screen's
 * ordinary path, never a strike and never a discard: it says only that nobody could tell.
 */
export type RepoDeveloperStanding = 'developer' | 'external' | 'unknown';

/**
 * The port that answers it: `readRepoDeveloper` (`@developerz/github`) in production. The
 * identity is the author read off a signed delivery, never a word in the text. `userId` is
 * GitHub's numeric id where the door kept it, since a login can be renamed and re-registered.
 */
export type IsRepoDeveloper = (input: {
  readonly owner: string;
  readonly repo: string;
  readonly login: string;
  readonly userId: number | null;
}) => Promise<RepoDeveloperStanding>;

/**
 * The port, over every repository a text names. `developer` only when EVERY one answers it
 * (membership of another org grants nothing, #7785); any `external` is `external`; else
 * `unknown`. No login, no repo or a name that is not `owner/repo` never asks. A fault in the
 * port is `unknown`, reported through `onFault`: it widens nothing, so the text is screened.
 */
export async function repoDeveloperStanding(
  port: IsRepoDeveloper,
  input: {
    readonly repoFullNames: readonly string[];
    readonly login: string | null;
    readonly userId: number | null;
  },
  onFault: (err: unknown) => void,
): Promise<RepoDeveloperStanding> {
  const { login, userId } = input;
  if (login === null || login.trim() === '' || input.repoFullNames.length === 0) return 'unknown';
  const repos = input.repoFullNames.map((name) => name.split('/'));
  if (repos.some((parts) => parts.length !== 2 || parts.some((p) => p === ''))) return 'unknown';
  let answers: RepoDeveloperStanding[];
  try {
    answers = await Promise.all(
      repos.map(([owner, repo]) =>
        port({ owner: owner as string, repo: repo as string, login, userId }),
      ),
    );
  } catch (err) {
    onFault(err);
    return 'unknown';
  }
  if (answers.includes('external')) return 'external';
  return answers.every((a) => a === 'developer') ? 'developer' : 'unknown';
}

/**
 * Is an envelope's OWN text (title, problem, notes, criteria) first-party, so the box's run-start
 * screen skips it (#7785: developers get no friction)? A stamped provenance decides when there
 * is one: only `developer` is first-party, and anything else, `external` and a value this build
 * does not know included, screens. With none, a live session is: `via === 'session'` is the one
 * field that evidences a human (`DispatchedBySchema`). `kind: 'user'` alone does not: it is an auth
 * tier, and an agent's PAT and a box's own token arrive as `user` too. So a person's PAT-driven CLI
 * task screens, since it cannot be told apart. Unknown screens. Machine fields only; no text
 * reaches here.
 */
export function envelopeTextIsFirstParty(
  envelope: Pick<TaskEnvelope, 'dispatched_by' | 'text_provenance'>,
): boolean {
  const stamped = envelope.text_provenance?.author_standing;
  if (stamped !== undefined) return stamped === 'developer';
  return envelope.dispatched_by.kind === 'user' && envelope.dispatched_by.via === 'session';
}

/**
 * The provenance a merge target carries once a source task's text is folded into its `problem`
 * (the queue judge's merge): unchanged only when BOTH sides are first-party. Otherwise `merged`,
 * which outranks `dispatched_by`, so a developer's task cannot carry an outsider's words past
 * the run-start screen. It stays `merged` if the block is later stripped: that screens, the safe side.
 */
export function textProvenanceAfterMerge(
  target: Pick<TaskEnvelope, 'dispatched_by' | 'text_provenance'>,
  source: Pick<TaskEnvelope, 'dispatched_by' | 'text_provenance'>,
): Pick<TaskEnvelope, 'text_provenance'> {
  if (envelopeTextIsFirstParty(target) && envelopeTextIsFirstParty(source)) {
    return target.text_provenance === undefined ? {} : { text_provenance: target.text_provenance };
  }
  return { text_provenance: { author_standing: 'merged' } };
}

/** Whose words a task's text is, as one class. `external` is what the fetch gate reads. */
export type TextProvenanceClass = 'first_party' | 'external';

/**
 * Pure, over machine fields. A stamped class decides; a stamp with none follows
 * {@link envelopeTextIsFirstParty}'s rule that only `developer` is first party. With no stamp, a
 * person dispatching is first party (no API change), and a triage mint is external, since every
 * row minted before the class and every mixed batch reads that way: a batch of only the platform's
 * asks is stamped `first_party` (`triageDispatchFields`, `@developerz/agent`). Another platform
 * lane's own text is first party.
 */
export function textProvenanceClassOf(
  envelope: Pick<TaskEnvelope, 'dispatched_by' | 'text_provenance'>,
): TextProvenanceClass {
  const stamped = envelope.text_provenance;
  if (stamped?.class !== undefined) return stamped.class;
  if (stamped !== undefined)
    return stamped.author_standing === 'developer' ? 'first_party' : 'external';
  const { dispatched_by: by } = envelope;
  return by.kind === 'system' && by.source === 'raw_task_triage' ? 'external' : 'first_party';
}

/**
 * The provenance a machine RE-FILE of a task's text carries (`raw_tasks.text_provenance`). THE
 * LAUNDERING HOLE: a close files its goal again as a `janitor` ask, `platform` trust, quoting
 * `envelope.problem`, so an outsider's words came back reading as ours. Spread into the draft: a
 * first-party task states nothing and its re-file stays the platform's.
 */
export function refileTextProvenance(
  envelope: Pick<TaskEnvelope, 'dispatched_by' | 'text_provenance'>,
): { readonly textProvenance?: NonNullable<TaskEnvelope['text_provenance']> } {
  if (textProvenanceClassOf(envelope) !== 'external') return {};
  return {
    textProvenance: envelope.text_provenance ?? { author_standing: 'external', class: 'external' },
  };
}

// ─── H5 #5403: the push doors (third-party text a WORKING agent is handed) ────

/**
 * The doors third-party text reaches a working agent through OTHER than a raw task
 * (H5 #5403, plan 101 slice 09 "working agents only see screened text"). Context for
 * the screen model only, exactly like a raw task's source: the verdict store keys on
 * the TEXT, so a door never moves a verdict.
 *
 *   - `gh_event`: a GitHub webhook payload's free text (issue/PR title and body, a
 *     comment, a review, a release, a head commit message) booting an agent run.
 *   - `gh_comment`: a human comment in the session subject's coalesce window.
 *   - `pr_feedback`: a PR comment or a third-party reviewer's finding a babysit fix
 *     round is briefed with.
 *
 * H7 #5444 — the BOX-side doors, screened through the `screen_items` verb (the box
 * fetches the text live, the mothership screens it on the account's key):
 *
 *   - `pr_review`: what the AI reviewer reads about the PR it reviews — its title and
 *     description, the linked issue, prior discussion on it, its CI check names.
 *   - `review_thread`: a comment in a review thread the reply sweep answers.
 *   - `task_envelope`: a task's own title / problem / operator notes / conversation
 *     digest, screened once at run start before any renderer quotes it.
 */
export const SCREEN_PUSH_DOORS = [
  'gh_event',
  'gh_comment',
  'pr_feedback',
  'pr_review',
  'review_thread',
  'task_envelope',
] as const;
export type ScreenPushDoor = (typeof SCREEN_PUSH_DOORS)[number];

/** Every door the screen is told a text came in by: a raw task's, or a push door. */
export type ScreenSource = RawTaskSource | ScreenPushDoor;

/**
 * The kinds a BOX may ask the mothership to screen (H7 #5444, the `screen_items` verb,
 * `@developerz/nats`): third-party text a runner fetched live or holds on its task
 * envelope. The wire admits ONLY these — a box never screens a `gh_event` kind, which
 * the mothership screens itself before the run exists.
 *
 *   - `pr_title` / `pr_description`: the reviewed PR's own title and body.
 *   - `issue_title` / `issue_body`: the issue the PR says it closes (the task contract).
 *   - `prior_discussion_comment`: an existing review-thread comment the reviewer reads.
 *   - `ci_check_name`: a check-run name — fork-controlled on a fork PR.
 *   - `review_thread_comment`: a comment in a thread the reply sweep answers.
 *   - `task_title` / `task_problem` / `task_operator_notes` / `task_conversation_digest`:
 *     the task envelope's free text, screened once at run start.
 *   - `task_acceptance`: one line of the envelope's `acceptance_criteria`, screened with the rest
 *     at run start. A discarded one is WITHHELD in place, never fatal: it is one line of the
 *     definition of done, not the ask (`apps/runner/src/envelope-screen.ts`).
 *   - `task_carry_over`: the assignment's `carry_over` — earlier runs' prose and the human
 *     messages they were sent, screened with the rest at run start. A withheld one is
 *     DROPPED, never fatal: it is context, not the ask (`apps/runner/src/envelope-screen.ts`).
 */
export const BOX_SCREEN_ITEM_KINDS = [
  'pr_title',
  'pr_description',
  'issue_title',
  'issue_body',
  'prior_discussion_comment',
  'ci_check_name',
  'review_thread_comment',
  'task_title',
  'task_problem',
  'task_operator_notes',
  'task_conversation_digest',
  'task_acceptance',
  'task_carry_over',
] as const;
export const BoxScreenItemKindSchema = z.enum(BOX_SCREEN_ITEM_KINDS);
export type BoxScreenItemKind = z.infer<typeof BoxScreenItemKindSchema>;

/**
 * WHICH third-party field one screened item was, for the `prompt.item_screened` audit
 * row. CLOSED: a count per kind is the question the row answers ("how many review
 * bodies did the screen withhold this week"), and a free string would split one kind
 * across spellings.
 */
export const SCREEN_ITEM_KINDS = [
  // A webhook payload booting an agent run (`gh_event`).
  'event_title',
  'event_body',
  'event_comment',
  'event_review',
  'event_release_name',
  'event_release_body',
  'event_head_commit',
  'event_payload',
  // The session subject's coalesce-window comments (`gh_comment`).
  'subject_comment',
  // A babysit fix round's brief (`pr_feedback`).
  'pr_comment',
  'pr_finding',
  'pr_summary_finding',
  'pr_check_name',
  // H7 #5444: the box-side fields, screened through `screen_items` — see BOX_SCREEN_ITEM_KINDS.
  ...BOX_SCREEN_ITEM_KINDS,
] as const;
export const ScreenItemKindSchema = z.enum(SCREEN_ITEM_KINDS);
export type ScreenItemKind = z.infer<typeof ScreenItemKindSchema>;

/**
 * The origins the box's run-start screen (`withTaskTextScreen`,
 * `apps/runner/src/envelope-screen.ts`) passes straight through, because no model on the
 * box reads their envelope text unscreened. Two kinds, for two reasons:
 *
 *   - THE REVIEW LANES (`pr_review`, `pr_review_reply`) screen their own live third-party
 *     text IN-LANE, under their own policy (a withheld PR body blocks approve; a withheld
 *     thread comment skips the thread) — a run-start discard would fail a review outright.
 *   - THE MODEL-CALL OFFERS (`isModelCallOrigin`: `triage_judge`, `kb_fold`, and whatever
 *     origin next requires the `model_call` capability) render NONE of it: the model-call
 *     lane (`apps/runner/src/modes/worker/model-call/`) sends the model only the prompt it
 *     fetches (`model_call_prompt`), never the envelope's title or problem, which are a fixed
 *     platform string. Screening that string judged text no model reads, and a verdict is
 *     cached per text hash (`screen_verdicts`), so ONE `discard / spam` on kb_fold's string
 *     failed every kb_fold offer `screen_discarded` (#7583). DERIVED from
 *     `isModelCallOrigin`, never listed, so a new model-call origin cannot be screened for
 *     nothing again.
 *
 * Readers: that runner skip (`SCREENS_IN_LANE`, same file), and the claim gate's EARLY
 * screen check, below.
 *
 * THE CLAIM GATE COVERS THESE ORIGINS TOO (`apps/worker` `dispatch/screen-key-gate.ts`,
 * plan 101 09-10). Their in-lane screens ride the mothership's `screen_items` responder
 * (`apps/worker/src/fleet/screen-items.ts`), which is LANE-UNAWARE: it resolves
 * `accountScreenResolver` (`apps/worker/src/babysit/brief-screen-wiring.ts`), and that
 * resolver reads `SCREEN_ROUTE` (`apps/worker/src/dispatch/screen-route.ts`) — the
 * `screen` purpose and the `fast` tier, over the account's own `screen` pin. NOT the
 * `default` catch-all row, and NOT the `general` tier: `screen` is a PIN-ONLY purpose
 * (`isPinOnlyPurpose`, `value-objects/job-class-routing.ts`), so `isLaneFencePurpose`
 * refuses it — no key is fenced to `screen`, and a key fenced to `review` serves the
 * review WORK but never the screen.
 *
 * So the claim asks the SAME question the responder will ask, BEFORE the credential
 * resolves, for exactly these origins (`refusePreResolve`,
 * `apps/worker/src/dispatch/claim-gates.ts`): an account holding no usable screening key
 * parks the claim `no_screen_key` — the hold a screening key wakes — instead of
 * letting the credential resolver throw `review_awaiting_model` (the hold the re-check
 * clock wakes) and naming the missing REVIEW MODEL. Every other origin keeps the
 * post-resolve position: its pool-cooling refusal still precedes its screen refusal.
 *
 * THE MODEL-CALL OFFERS RIDE THE EARLY POSITION TOO, as members of this one set (#7583).
 * Their claim was already gated on a screening key AFTER the resolve; now it is asked
 * BEFORE, so on an account both walled and keyless the park names `no_screen_key` rather
 * than `pool_cooling`. Either way the offer is not claimed and its caller's window
 * take-back runs. That an offer is gated on a screening key no screen will use is older
 * than this set's model-call half, and is not changed here.
 */
export const TASK_TEXT_SCREENED_IN_LANE: ReadonlySet<TaskOrigin> = new Set<TaskOrigin>([
  TaskOrigin.PrReview,
  TaskOrigin.PrReviewReply,
  ...TaskOriginSchema.options.filter(isModelCallOrigin),
]);

/**
 * The door each box-side kind came in by — context for the screen model, derived by
 * the RESPONDER from the kind (`apps/worker/src/fleet/screen-items.ts`), never sent by
 * the box, so a box cannot pick the framing its own text is judged under.
 */
export const BOX_SCREEN_ITEM_DOOR = {
  pr_title: 'pr_review',
  pr_description: 'pr_review',
  issue_title: 'pr_review',
  issue_body: 'pr_review',
  prior_discussion_comment: 'pr_review',
  ci_check_name: 'pr_review',
  review_thread_comment: 'review_thread',
  task_title: 'task_envelope',
  task_problem: 'task_envelope',
  task_operator_notes: 'task_envelope',
  task_conversation_digest: 'task_envelope',
  task_acceptance: 'task_envelope',
  task_carry_over: 'task_envelope',
} as const satisfies Record<BoxScreenItemKind, ScreenPushDoor>;

/**
 * Why an item is NOT in a working agent's prompt: the screen discarded it, or the
 * screen could not answer (`unavailable`) and the caller chose to withhold rather than
 * re-drive. Never "rendered unscreened" — that is not a member.
 */
export type ScreenWithheldReason = ScreenDiscardReason | 'unavailable';
